/**
 * User management component for admin panel.
 *
 * Comprehensive user management interface with CRUD operations, filtering,
 * pagination, and bulk actions for user accounts.
 *
 * Route: `/admin/users`
 * Requires authentication: Yes (admin only)
 *
 * @module UserManager
 */

import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Search,
  Mail,
  Shield,
  Ban,
  CheckCircle,
  Edit,
  Trash2,
  Eye,
  Download,
  Loader2,
} from 'lucide-react';
import { CEFR_LEVELS, USER_ROLES, USER_STATUSES } from '@/lib/constants';
import { formatDateTime } from '@/lib/dateUtils';
import { cn } from '@/lib/utils';
import {
  User,
  UserRole,
  getUsers as getUsersApi,
  updateUser as updateUserApi,
  updateUserRole as updateUserRoleApi,
  updateUserStatus as updateUserStatusApi,
  deleteUser as deleteUserApi,
} from '@/lib/userApi';
import { logger } from '@/lib/logger';
import { useAuth } from '@/hooks/useAuth';

// Helper: Check if user matches search term
const matchesSearchTerm = (user: User, term: string): boolean => {
  const lowerTerm = term.toLowerCase();
  return (
    user.username.toLowerCase().includes(lowerTerm) ||
    user.email.toLowerCase().includes(lowerTerm) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(lowerTerm)
  );
};

// Helper: Check if user matches status filter
const matchesStatusFilter = (user: User, status: string): boolean => {
  if (status === 'all') return true;
  if (status === 'active') return user.isActive;
  if (status === 'inactive') return !user.isActive;
  if (status === 'verified') return user.isVerified;
  if (status === 'unverified') return !user.isVerified;
  return true;
};

// Helper: Filter users based on all criteria
const filterUser = (
  user: User,
  searchTerm: string,
  filterRole: string,
  filterLevel: string,
  filterStatus: string,
): boolean => {
  const matchesSearch = matchesSearchTerm(user, searchTerm);
  const matchesRole = filterRole === 'all' || user.role === filterRole;
  const matchesLevel =
    filterLevel === 'all' || (user.level && user.level === filterLevel);
  const matchesStatus = matchesStatusFilter(user, filterStatus);

  return matchesSearch && matchesRole && matchesLevel && matchesStatus;
};

// Helper: Fetch users from API
const fetchUsersData = async (
  setUsers: (users: User[]) => void,
  setLoading: (loading: boolean) => void,
): Promise<void> => {
  try {
    setLoading(true);
    logger.event('admin.users.fetch.started', {}, 'info', 'Loading users for admin user manager');
    const data = await getUsersApi();
    setUsers(data);
    logger.event(
      'admin.users.fetch.completed',
      { userCount: data.length },
      'info',
      'Loaded users for admin user manager',
    );
  } catch (error) {
    logger.error('Error fetching users', error, {
      event: 'admin.users.fetch.failed',
    });
    toast.error('Failed to load users');
  } finally {
    setLoading(false);
  }
};

// Helper: Get activation success message
const getActivationMessage = (username: string): string => {
  return `User ${username} activated successfully`;
};

// Helper: Get deactivation success message
const getDeactivationMessage = (username: string): string => {
  return `User ${username} deactivated successfully`;
};

// Helper: Get role icon
const getRoleIcon = (role: string) => {
  switch (role) {
    case 'admin':
      return <Shield className="h-4 w-4" />;
    case 'instructor':
      return <CheckCircle className="h-4 w-4" />;
    default:
      return null;
  }
};

// Helper: Get role badge variant
const getRoleBadgeVariant = (role: string) => {
  switch (role) {
    case 'admin':
      return 'destructive';
    case 'instructor':
      return 'default';
    default:
      return 'secondary';
  }
};

// Helper: Get active status badge classes
const getActiveStatusBadgeClasses = (): string => {
  return 'bg-primary/10 text-primary border-primary/20';
};

// Helper: Get inactive status badge classes
const getInactiveStatusBadgeClasses = (): string => {
  return 'bg-muted text-muted-foreground border-border';
};

// Helper: Calculate user statistics
const calculateUserStats = (users: User[]) => {
  return {
    total: users.length,
    active: users.filter((u) => u.isActive).length,
    verified: users.filter((u) => u.isVerified).length,
    students: users.filter((u) => u.role === 'student').length,
    instructors: users.filter((u) => u.role === 'instructor').length,
    admins: users.filter((u) => u.role === 'admin').length,
  };
};

// Helper: Calculate pagination values
const calculatePagination = (
  filteredUsers: User[],
  currentPage: number,
  USERS_PER_PAGE: number,
) => {
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const endIndex = startIndex + USERS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);
  return { totalPages, currentUsers };
};

// Helper: Toggle user active status
const toggleUserActiveStatus = async (
  userId: string,
  users: User[],
  setUsers: (users: User[] | ((prev: User[]) => User[])) => void,
): Promise<void> => {
  const user = users.find((u) => u.id === userId);
  if (!user) return;

  const newActiveState = !user.isActive;
  logger.event(
    'admin.users.status_update.started',
    {
      targetUserId: userId,
      username: user.username,
      nextIsActive: newActiveState,
    },
    'info',
    'Updating user active status',
  );
  await updateUserStatusApi(userId, newActiveState);
  setUsers(
    users.map((u) =>
      u.id === userId ? { ...u, isActive: newActiveState } : u,
    ),
  );
  logger.event(
    'admin.users.status_update.completed',
    {
      targetUserId: userId,
      username: user.username,
      nextIsActive: newActiveState,
    },
    'info',
    'Updated user active status',
  );
  toast.success(
    newActiveState
      ? getActivationMessage(user.username)
      : getDeactivationMessage(user.username),
  );
};

// Helper: Delete user
const deleteUserById = async (
  userId: string,
  username: string,
  users: User[],
  setUsers: (users: User[] | ((prev: User[]) => User[])) => void,
  setDeleteConfirmUser: (user: User | null) => void,
): Promise<void> => {
  logger.event(
    'admin.users.delete.started',
    {
      targetUserId: userId,
      username,
    },
    'warn',
    'Deleting user from admin user manager',
  );
  await deleteUserApi(userId);
  setUsers(users.filter((u) => u.id !== userId));
  setDeleteConfirmUser(null);
  logger.event(
    'admin.users.delete.completed',
    {
      targetUserId: userId,
      username,
    },
    'info',
    'Deleted user from admin user manager',
  );
  toast.success(`User ${username} deleted successfully`);
};

// Helper: Delete several users in one action, reusing the single-delete endpoint.
// Tolerates partial failures: successfully deleted rows are removed and the
// outcome is summarised in a single toast.
const batchDeleteUsers = async (
  userIds: string[],
  setUsers: (users: User[] | ((prev: User[]) => User[])) => void,
  setSelectedUsers: (ids: string[]) => void,
): Promise<void> => {
  logger.event(
    'admin.users.batch_delete.started',
    { count: userIds.length },
    'warn',
    'Batch deleting users from admin user manager',
  );

  const results = await Promise.allSettled(userIds.map((id) => deleteUserApi(id)));
  const deletedIds = userIds.filter((_, i) => results[i].status === 'fulfilled');
  const failedCount = userIds.length - deletedIds.length;

  if (deletedIds.length > 0) {
    const deletedSet = new Set(deletedIds);
    setUsers((prev) => prev.filter((u) => !deletedSet.has(u.id)));
  }
  setSelectedUsers([]);

  logger.event(
    'admin.users.batch_delete.completed',
    { requested: userIds.length, deleted: deletedIds.length, failed: failedCount },
    failedCount > 0 ? 'warn' : 'info',
    'Batch delete completed',
  );

  if (failedCount === 0) {
    toast.success(
      `Deleted ${deletedIds.length} user${deletedIds.length === 1 ? '' : 's'} successfully`,
    );
  } else if (deletedIds.length === 0) {
    toast.error(`Failed to delete ${failedCount} user${failedCount === 1 ? '' : 's'}`);
  } else {
    toast.error(`Deleted ${deletedIds.length}, failed to delete ${failedCount}`);
  }
};

// Helper: Process user update
const processUserUpdate = async (
  userId: string,
  updates: { role?: UserRole; isActive?: boolean },
  users: User[],
  setUsers: (users: User[] | ((prev: User[]) => User[])) => void,
): Promise<User | null> => {
  const user = users.find((u) => u.id === userId);
  if (!user) return null;

  const hasUpdates = updates.role !== undefined || updates.isActive !== undefined;
  if (!hasUpdates) return null;

  logger.event(
    'admin.users.update.started',
    {
      targetUserId: userId,
      username: user.username,
      updates,
    },
    'info',
    'Updating user in admin user manager',
  );
  await applyUserUpdates(userId, updates);
  const updatedUser = buildUpdatedUser(user, updates);
  setUsers(updateUserInArray(users, userId, updatedUser));
  logger.event(
    'admin.users.update.completed',
    {
      targetUserId: userId,
      username: user.username,
      updates,
    },
    'info',
    'Updated user in admin user manager',
  );

  return updatedUser;
};

// Helper: Handle user update with error handling
interface HandleUserUpdateParams {
  userId: string;
  updates: { role?: UserRole; isActive?: boolean };
  users: User[];
  setUsers: (users: User[] | ((prev: User[]) => User[])) => void;
  viewingUser: User | null;
  editingUser: User | null;
  editConfirmData: { user: User; newRole: string; newStatus: string } | null;
  setViewingUser: (user: User | null) => void;
  setEditingUser: (user: User | null) => void;
  setEditConfirmData: (data: { user: User; newRole: string; newStatus: string } | null) => void;
}

const handleUserUpdateWithErrorHandling = async (
  params: HandleUserUpdateParams,
): Promise<void> => {
  try {
    const updatedUser = await processUserUpdate(
      params.userId,
      params.updates,
      params.users,
      params.setUsers,
    );
    if (!updatedUser) return;
    updateUserRelatedState({
      userId: params.userId,
      updatedUser,
      viewingUser: params.viewingUser,
      editingUser: params.editingUser,
      editConfirmData: params.editConfirmData,
      setViewingUser: params.setViewingUser,
      setEditingUser: params.setEditingUser,
      setEditConfirmData: params.setEditConfirmData,
    });
    toast.success('User updated successfully');
  } catch (error) {
    logger.error('Error updating user', error, {
      event: 'admin.users.update.failed',
      targetUserId: params.userId,
      updates: params.updates,
    });
    toast.error('Failed to update user');
  }
};

// Helper: Apply user updates via API
const applyUserUpdates = async (
  userId: string,
  updates: { role?: UserRole; isActive?: boolean },
): Promise<void> => {
  if (updates.role !== undefined && updates.isActive !== undefined) {
    await updateUserApi(userId, {
      userType: updates.role,
      isActive: updates.isActive,
    });
  } else if (updates.role !== undefined) {
    await updateUserRoleApi(userId, updates.role);
  } else if (updates.isActive !== undefined) {
    await updateUserStatusApi(userId, updates.isActive);
  }
};

// Helper: Build updated user object
const buildUpdatedUser = (
  user: User,
  updates: { role?: UserRole; isActive?: boolean },
): User => {
  const updatedUser = { ...user };
  if (updates.role !== undefined) {
    updatedUser.role = updates.role;
  }
  if (updates.isActive !== undefined) {
    updatedUser.isActive = updates.isActive;
  }
  return updatedUser;
};

// Helper: Update user in users array
const updateUserInArray = (
  users: User[],
  userId: string,
  updatedUser: User,
): User[] => {
  return users.map((u) => (u.id === userId ? updatedUser : u));
};

// Helper: Update related state after user update
interface UpdateStateParams {
  userId: string;
  updatedUser: User;
  viewingUser: User | null;
  editingUser: User | null;
  editConfirmData: { user: User; newRole: string; newStatus: string } | null;
  setViewingUser: (user: User | null) => void;
  setEditingUser: (user: User | null) => void;
  setEditConfirmData: (data: { user: User; newRole: string; newStatus: string } | null) => void;
}

const updateUserRelatedState = (params: UpdateStateParams): void => {
  if (params.viewingUser?.id === params.userId) {
    params.setViewingUser(params.updatedUser);
  }
  if (params.editingUser?.id === params.userId) {
    params.setEditingUser(null);
  }
  if (params.editConfirmData) {
    params.setEditConfirmData(null);
  }
};

const USERS_PER_PAGE = 10;
const FILTER_CONSTANTS = {
  levels: CEFR_LEVELS,
  roles: USER_ROLES,
  statuses: USER_STATUSES,
};


// Helper: Create handlers and calculate stats
const createHandlersAndStats = (params: CreateHandlersParams) => {
  const handlers = createUserHandlers(params);
  const stats = calculateUserStats(params.users);
  return { handlers, stats };
};

// Helper: Prepare all component data (filtering, pagination, handlers, stats)
interface PrepareComponentDataParams {
  users: User[];
  searchTerm: string;
  filterRole: string;
  filterLevel: string;
  filterStatus: string;
  currentPage: number;
  setUsers: (users: User[] | ((prev: User[]) => User[])) => void;
  deleteConfirmUser: User | null;
  setDeleteConfirmUser: (user: User | null) => void;
  viewingUser: User | null;
  editingUser: User | null;
  editConfirmData: { user: User; newRole: string; newStatus: string } | null;
  setViewingUser: (user: User | null) => void;
  setEditingUser: (user: User | null) => void;
  setEditConfirmData: (data: { user: User; newRole: string; newStatus: string } | null) => void;
}

const prepareComponentData = (params: PrepareComponentDataParams) => {
  const filtered = params.users.filter((user) =>
    filterUser(user, params.searchTerm, params.filterRole, params.filterLevel, params.filterStatus),
  );
  const { totalPages, currentUsers } = calculatePagination(filtered, params.currentPage, USERS_PER_PAGE);
  const { handlers, stats } = createHandlersAndStats({
    users: params.users,
    setUsers: params.setUsers,
    deleteConfirmUser: params.deleteConfirmUser,
    setDeleteConfirmUser: params.setDeleteConfirmUser,
    viewingUser: params.viewingUser,
    editingUser: params.editingUser,
    editConfirmData: params.editConfirmData,
    setViewingUser: params.setViewingUser,
    setEditingUser: params.setEditingUser,
    setEditConfirmData: params.setEditConfirmData,
  });
  return { filteredUsers: filtered, totalPages, currentUsers, handlers, stats };
};

// Helper: Create user management handlers
interface CreateHandlersParams {
  users: User[];
  setUsers: (users: User[] | ((prev: User[]) => User[])) => void;
  deleteConfirmUser: User | null;
  setDeleteConfirmUser: (user: User | null) => void;
  viewingUser: User | null;
  editingUser: User | null;
  editConfirmData: { user: User; newRole: string; newStatus: string } | null;
  setViewingUser: (user: User | null) => void;
  setEditingUser: (user: User | null) => void;
  setEditConfirmData: (data: { user: User; newRole: string; newStatus: string } | null) => void;
}

const createUserHandlers = (params: CreateHandlersParams) => {
  const handleToggleActive = async (userId: string) => {
    try {
      await toggleUserActiveStatus(userId, params.users, params.setUsers);
    } catch (error) {
      logger.error('Error updating user status', error, {
        event: 'admin.users.status_update.failed',
        targetUserId: userId,
      });
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    if (!params.deleteConfirmUser) return;
    try {
      await deleteUserById(
        params.deleteConfirmUser.id,
        params.deleteConfirmUser.username,
        params.users,
        params.setUsers,
        params.setDeleteConfirmUser,
      );
    } catch (error) {
      logger.error('Error deleting user', error, {
        event: 'admin.users.delete.failed',
        targetUserId: params.deleteConfirmUser.id,
        username: params.deleteConfirmUser.username,
      });
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateUser = async (
    userId: string,
    updates: { role?: UserRole; isActive?: boolean },
  ) => {
    await handleUserUpdateWithErrorHandling({
      userId,
      updates,
      users: params.users,
      setUsers: params.setUsers,
      viewingUser: params.viewingUser,
      editingUser: params.editingUser,
      editConfirmData: params.editConfirmData,
      setViewingUser: params.setViewingUser,
      setEditingUser: params.setEditingUser,
      setEditConfirmData: params.setEditConfirmData,
    });
  };

  return { handleToggleActive, handleDeleteUser, handleUpdateUser };
};

export default function UserManager() {
  const { user: currentUser } = useAuth();

  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  const [editConfirmData, setEditConfirmData] = useState<{
    user: User;
    newRole: string;
    newStatus: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Constants
  const { levels, roles, statuses } = FILTER_CONSTANTS;

  // Initialize data and reset pagination
  useEffect(() => {
    fetchUsersData(setUsers, setLoading);
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, filterLevel, filterStatus]);

  // Prepare all component data
  const {
    filteredUsers,
    totalPages,
    currentUsers,
    handlers: { handleToggleActive, handleDeleteUser, handleUpdateUser },
    stats,
  } = useMemo(
    () =>
      prepareComponentData({
        users,
        searchTerm,
        filterRole,
        filterLevel,
        filterStatus,
        currentPage,
        setUsers,
        deleteConfirmUser,
        setDeleteConfirmUser,
        viewingUser,
        editingUser,
        editConfirmData,
        setViewingUser,
        setEditingUser,
        setEditConfirmData,
      }),
    [
      users,
      searchTerm,
      filterRole,
      filterLevel,
      filterStatus,
      currentPage,
      setUsers,
      deleteConfirmUser,
      setDeleteConfirmUser,
      viewingUser,
      editingUser,
      editConfirmData,
      setViewingUser,
      setEditingUser,
      setEditConfirmData,
    ],
  );

  // Handle checkbox change for user selection
  const handleUserSelectionChange = (userId: string, checked: boolean | string | undefined) => {
    if (checked === true) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  // Selected users that may actually be deleted (an admin cannot delete their own account)
  const deletableSelectedIds = useMemo(
    () => selectedUsers.filter((id) => id !== currentUser?.id),
    [selectedUsers, currentUser?.id],
  );

  // Delete every selected user (minus self) via the batch helper
  const handleBatchDelete = async () => {
    if (deletableSelectedIds.length === 0) return;
    setIsBatchDeleting(true);
    try {
      await batchDeleteUsers(deletableSelectedIds, setUsers, setSelectedUsers);
    } catch (error) {
      logger.error('Error during batch user deletion', error, {
        event: 'admin.users.batch_delete.failed',
      });
      toast.error('Failed to delete selected users');
    } finally {
      setIsBatchDeleting(false);
      setBatchDeleteOpen(false);
    }
  };

  // Handle keyboard events for status badge
  const handleStatusBadgeKeyDown = (userId: string, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleActive(userId);
    }
  };

  // Render table body content
  const renderTableBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={9} className="text-center py-8">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="font-ui">Loading...</span>
            </div>
          </TableCell>
        </TableRow>
      );
    }
    if (filteredUsers.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={9}
            className="text-center py-8 text-muted-foreground"
          >
            <span className="font-ui">No users found</span>
          </TableCell>
        </TableRow>
      );
    }
    return currentUsers.map((user) => (
      <TableRow key={user.id}>
        <TableCell>
          <Checkbox
            checked={selectedUsers.includes(user.id)}
            onCheckedChange={(checked) => handleUserSelectionChange(user.id, checked)}
          />
        </TableCell>
        <TableCell className="font-ui text-xs text-muted-foreground truncate max-w-[100px]" title={user.id}>
          {user.id.split('-')[0]}...
        </TableCell>
        <TableCell className="font-ui">{user.email}</TableCell>
        <TableCell className="font-ui">
          {user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.username || 'N/A'}
        </TableCell>
        <TableCell>
          <Badge
            variant={getRoleBadgeVariant(user.role)}
            className="font-ui capitalize"
          >
            {getRoleIcon(user.role)} {user.role}
          </Badge>
        </TableCell>
        <TableCell className="font-ui">{user.level || 'N/A'}</TableCell>
        <TableCell>
          <Badge
            variant="outline"
            className={cn(
              'font-ui capitalize cursor-pointer',
              user.isActive
                ? getActiveStatusBadgeClasses()
                : getInactiveStatusBadgeClasses(),
            )}
            onClick={() => handleToggleActive(user.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => handleStatusBadgeKeyDown(user.id, e)}
            aria-label={`Toggle user status to ${user.isActive ? 'inactive' : 'active'}`}
          >
            {user.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
        <TableCell className="font-ui whitespace-nowrap">
          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewingUser(user)}
              title="View"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditingUser(user)}
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDeleteConfirmUser(user)}
              title="Delete User"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-ui text-2xl font-bold text-primary">
              {loading ? <Loader2 className="h-6 w-6 animate-spin inline" /> : stats.total}
            </p>
            <p className="font-ui text-sm text-muted-foreground">Total Users</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-ui text-2xl font-bold text-primary">
              {loading ? <Loader2 className="h-6 w-6 animate-spin inline" /> : stats.active}
            </p>
            <p className="font-ui text-sm text-muted-foreground">Active</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-ui text-2xl font-bold text-primary">
              {loading ? <Loader2 className="h-6 w-6 animate-spin inline" /> : stats.students}
            </p>
            <p className="font-ui text-sm text-muted-foreground">Students</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-ui text-2xl font-bold text-primary">
              {loading ? <Loader2 className="h-6 w-6 animate-spin inline" /> : stats.instructors}
            </p>
            <p className="font-ui text-sm text-muted-foreground">Instructors</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <p className="font-ui text-2xl font-bold text-primary">
              {loading ? <Loader2 className="h-6 w-6 animate-spin inline" /> : stats.admins}
            </p>
            <p className="font-ui text-sm text-muted-foreground">Admins</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-ui">User Management</CardTitle>
              <CardDescription className="font-ui">
                Manage user accounts, roles, and permissions
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                {levels.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Actions */}
          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              <span className="font-ui text-sm font-medium">
                {selectedUsers.length} user{selectedUsers.length === 1 ? '' : 's'} selected
              </span>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Send Email
              </Button>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export Data
              </Button>
              <Button variant="destructive" size="sm" className="flex items-center gap-2">
                <Ban className="h-4 w-4" />
                Deactivate
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="flex items-center gap-2"
                onClick={() => setBatchDeleteOpen(true)}
                disabled={deletableSelectedIds.length === 0 || isBatchDeleting}
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </Button>
            </div>
          )}

          {/* Users Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedUsers.length === currentUsers.length &&
                        currentUsers.length > 0
                      }
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedUsers(currentUsers.map((u) => u.id));
                        } else {
                          setSelectedUsers([]);
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead className="font-ui">ID</TableHead>
                  <TableHead className="font-ui">Email</TableHead>
                  <TableHead className="font-ui">User</TableHead>
                  <TableHead className="font-ui">Role</TableHead>
                  <TableHead className="font-ui">Level</TableHead>
                  <TableHead className="font-ui">Status</TableHead>
                  <TableHead className="font-ui">Joined</TableHead>
                  <TableHead className="w-20 font-ui text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderTableBody()}</TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {filteredUsers.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="font-ui text-sm text-muted-foreground">
                Showing {currentUsers.length} of {filteredUsers.length} users (Page{' '}
                {currentPage} of {totalPages})
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                  >
                    <span className="font-ui">Previous</span>
                  </Button>
                  <span className="font-ui text-sm text-muted-foreground px-2">
                    {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    <span className="font-ui">Next</span>
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog
        open={!!viewingUser}
        onOpenChange={(open) => !open && setViewingUser(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-ui">User Details</DialogTitle>
            <DialogDescription className="font-ui">
              View detailed information about this user
            </DialogDescription>
          </DialogHeader>

          {viewingUser && (
            <div className="space-y-6">
              {/* User Profile */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={viewingUser.profileImageUrl} />
                  <AvatarFallback className="text-2xl">
                    {viewingUser.username[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="font-ui text-xl font-semibold">
                    {viewingUser.username}
                  </h3>
                  <p className="font-ui text-muted-foreground">{viewingUser.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={getRoleBadgeVariant(viewingUser.role)}>
                      <span className="font-ui">{viewingUser.role}</span>
                    </Badge>
                    <Badge variant="outline">
                      <span className="font-ui">{viewingUser.level || 'N/A'}</span>
                    </Badge>
                    <Badge
                      variant={viewingUser.isActive ? 'secondary' : 'outline'}
                    >
                      <span className="font-ui">
                        {viewingUser.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </Badge>
                  </div>
                </div>
              </div>

              {/* User Information Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-ui text-sm text-muted-foreground">User ID</p>
                  <p className="font-ui font-medium">{viewingUser.id}</p>
                </div>
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Username</p>
                  <p className="font-ui font-medium">{viewingUser.username}</p>
                </div>
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Email</p>
                  <p className="font-ui font-medium">{viewingUser.email}</p>
                </div>
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Role</p>
                  <p className="font-ui font-medium capitalize">{viewingUser.role}</p>
                </div>
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Account Status</p>
                  <p className="font-ui font-medium">
                    {viewingUser.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Created At</p>
                  <p className="font-ui font-medium">
                    {formatDateTime(viewingUser.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Last Login</p>
                  <p className="font-ui font-medium">
                    {viewingUser.lastLogin
                      ? formatDateTime(viewingUser.lastLogin)
                      : 'Never'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setViewingUser(null)}>
                  <span className="font-ui">Close</span>
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    setEditingUser(viewingUser);
                    setViewingUser(null);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  <span className="font-ui">Edit User</span>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={!!editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-ui">Edit User</DialogTitle>
            <DialogDescription className="font-ui">
              Update user role and account status
            </DialogDescription>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-3 pb-4 border-b">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={editingUser.profileImageUrl} />
                  <AvatarFallback>
                    {editingUser.username[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-ui font-semibold">{editingUser.username}</p>
                  <p className="font-ui text-sm text-muted-foreground">
                    {editingUser.email}
                  </p>
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <p className="font-ui text-sm text-muted-foreground mb-2">User Role</p>
                <Select
                  value={editingUser.role}
                  onValueChange={(
                    value: UserRole,
                  ) => {
                    setEditingUser({ ...editingUser, role: value });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Account Status */}
              <div>
                <p className="font-ui text-sm text-muted-foreground mb-2">
                  Account Status
                </p>
                <Select
                  value={editingUser.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) => {
                    setEditingUser({
                      ...editingUser,
                      isActive: value === 'active',
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Current Info */}
              <div className="p-3 bg-muted rounded-lg space-y-2">
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Current Role</p>
                  <p className="font-ui font-medium capitalize">{editingUser.role}</p>
                </div>
                <div>
                  <p className="font-ui text-sm text-muted-foreground">Current Status</p>
                  <p className="font-ui font-medium">
                    {editingUser.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditingUser(null)}>
                  <span className="font-ui">Cancel</span>
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    if (editingUser) {
                      setEditConfirmData({
                        user: editingUser,
                        newRole: editingUser.role,
                        newStatus: editingUser.isActive ? 'Active' : 'Inactive',
                      });
                    }
                  }}
                >
                  <span className="font-ui">Save Changes</span>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Confirmation Dialog */}
      <AlertDialog
        open={!!editConfirmData}
        onOpenChange={(open) => !open && setEditConfirmData(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">Confirm Changes</AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              You are about to update user {editConfirmData?.user.username} (
              {editConfirmData?.user.email})
              <br />
              <br />
              <strong>Changes:</strong>
              <ul className="list-disc list-inside mt-2 font-ui">
                <li>
                  Role: {editConfirmData?.user.role} → {editConfirmData?.newRole}
                </li>
                <li>
                  Status:{' '}
                  {editConfirmData?.user.isActive ? 'Active' : 'Inactive'} →{' '}
                  {editConfirmData?.newStatus}
                </li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (editConfirmData) {
                  const isActiveStatus = editConfirmData.newStatus === 'Active';
                  handleUpdateUser(editConfirmData.user.id, {
                    role: editConfirmData.newRole as
                      | 'student'
                      | 'instructor'
                      | 'admin',
                    isActive: isActiveStatus,
                  });
                }
              }}
            >
              <span className="font-ui">Save Changes</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirmUser}
        onOpenChange={(open) => !open && setDeleteConfirmUser(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              Are you sure you want to delete user {deleteConfirmUser?.username} (
              {deleteConfirmUser?.email})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span className="font-ui">Delete User</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Delete Confirmation Dialog */}
      <AlertDialog
        open={batchDeleteOpen}
        onOpenChange={(open) => !open && !isBatchDeleting && setBatchDeleteOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">Delete Selected Users</AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              Are you sure you want to delete {deletableSelectedIds.length} selected user
              {deletableSelectedIds.length === 1 ? '' : 's'}? This action cannot be undone.
              {selectedUsers.length !== deletableSelectedIds.length && (
                <>
                  <br />
                  <br />
                  Your own account is excluded and will not be deleted.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBatchDeleting}>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBatchDelete();
              }}
              disabled={isBatchDeleting || deletableSelectedIds.length === 0}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isBatchDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <span className="font-ui">
                Delete {deletableSelectedIds.length} User
                {deletableSelectedIds.length === 1 ? '' : 's'}
              </span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
